/**
 * Data layer for `delegated-payment-processing`. Thin typed fetch wrappers over /api/v1/delegated-payment-processing.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface DelegatedPaymentProcessingRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListDelegatedPaymentProcessingResponse {
	count: number;
	items: DelegatedPaymentProcessingRow[];
}

export interface CreateDelegatedPaymentProcessingBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/delegated-payment-processing";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListDelegatedPaymentProcessingResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListDelegatedPaymentProcessingResponse>(res);
}

export async function create(body: CreateDelegatedPaymentProcessingBody): Promise<DelegatedPaymentProcessingRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<DelegatedPaymentProcessingRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const delegatedPaymentProcessingQueryKeys = {
	all: ["delegated-payment-processing"] as const,
	list: () => [...delegatedPaymentProcessingQueryKeys.all] as const,
	detail: (id: string) => [...delegatedPaymentProcessingQueryKeys.all, id] as const,
};
